import { useEffect, useMemo, useState } from 'react';
import { ADMIN_STATUS_LABEL } from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

export interface OrderLinkTarget {
  name?: string;
  buyer?: string;
  phone?: string;
  address?: string;
  lat?: number | null;
  lng?: number | null;
}

export interface OrderHit {
  orderNo: string;
  receiverName: string;
  receiverPhone?: string;
  receiverAddress: string;
  receiverLat?: number | string | null;
  receiverLng?: number | string | null;
  status: string;
  createdAt: string;
}

const UNFINISHED_STATUSES = 'PENDING,ASSIGNED,ACCEPTED,PICKED_UP,IN_TRANSIT,FAILED';

function cleanDigits(s?: string | null): string {
  if (!s) return '';
  return s.replace(/\D/g, '');
}

export function checkPhoneMatch(orderPhone?: string | null, targetPhone?: string | null): boolean {
  if (!orderPhone || !targetPhone) return false;
  const d1 = cleanDigits(orderPhone);
  const d2 = cleanDigits(targetPhone);
  if (!d1 || !d2) return false;
  if (d1 === d2) return true;
  // 尾号匹配(针对国际区号写法, 如 021 xxx xxxx 与 +64 21 xxx xxxx)
  if (d1.length >= 7 && d2.length >= 7) {
    if (d1.slice(-7) === d2.slice(-7)) return true;
  }
  return false;
}

function normName(s?: string | null): string {
  if (!s) return '';
  return s.trim().toLowerCase().replace(/\s+/g, '');
}

export function checkNameMatch(orderName?: string | null, target?: OrderLinkTarget): boolean {
  if (!orderName || !target) return false;
  const on = normName(orderName);
  if (!on) return false;
  const names = [target.name, target.buyer]
    .map(normName)
    .filter((n): n is string => Boolean(n));
  return names.includes(on);
}

export function checkCoordMatch(
  orderLat?: number | string | null,
  orderLng?: number | string | null,
  targetLat?: number | null,
  targetLng?: number | null,
): boolean {
  if (orderLat == null || orderLng == null || targetLat == null || targetLng == null) return false;
  const oLat = Number(orderLat);
  const oLng = Number(orderLng);
  const tLat = Number(targetLat);
  const tLng = Number(targetLng);
  if (Number.isNaN(oLat) || Number.isNaN(oLng) || Number.isNaN(tLat) || Number.isNaN(tLng)) return false;
  return Math.abs(oLat - tLat) < 0.0001 && Math.abs(oLng - tLng) < 0.0001;
}

export function getOrderMatchDetails(order: OrderHit, target?: OrderLinkTarget) {
  const phoneMatch = checkPhoneMatch(order.receiverPhone, target?.phone);
  const nameMatch = checkNameMatch(order.receiverName, target);
  const coordMatch = checkCoordMatch(order.receiverLat, order.receiverLng, target?.lat, target?.lng);
  const isMatched = phoneMatch || nameMatch || coordMatch;
  const score = (phoneMatch ? 4 : 0) + (nameMatch ? 2 : 0) + (coordMatch ? 2 : 0);
  return { phoneMatch, nameMatch, coordMatch, isMatched, score };
}

export function sortOrdersByPriority(orders: OrderHit[], target?: OrderLinkTarget): OrderHit[] {
  return [...orders].sort((a, b) => {
    const ma = getOrderMatchDetails(a, target);
    const mb = getOrderMatchDetails(b, target);
    if (ma.score !== mb.score) {
      return mb.score - ma.score;
    }
    // 没有匹配或匹配权重相同时, 按照订单号排序显示(降序让新订单在前)
    return b.orderNo.localeCompare(a.orderNo);
  });
}

/**
 * 接龙导入选择关联订单组件。
 *
 * ★ 规则:
 *   1. 只选择当前「尚未完成的订单」(非历史已送达/已取消等终态单)。
 *   2. 优先显示电话一样、或者名字一样(忽略大小写)或者地址坐标一样的订单。
 *   3. 如果没有一样的就按照订单号排序显示。
 */
export function OrderLinkPicker({
  initial = '',
  target,
  onPick,
  onClose,
}: {
  /** 兼容/备选搜索词 */
  initial?: string;
  /** 当前行收件人信息(电话/姓名/坐标)用于匹配 */
  target?: OrderLinkTarget;
  onPick: (orderNo: string) => void;
  onClose: () => void;
}) {
  const [filterText, setFilterText] = useState('');
  const [orders, setOrders] = useState<OrderHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function loadUnfinishedOrders(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ data: OrderHit[] }>(`/orders?status=${UNFINISHED_STATUSES}&limit=300`);
      setOrders(r.data);
      if (r.data.length === 0) {
        setErr('当前没有未完成的订单');
      }
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '加载未完成订单失败');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void loadUnfinishedOrders();
  }, []);

  const displayedOrders = useMemo(() => {
    if (!orders) return [];
    const keyword = filterText.trim().toLowerCase();
    const filtered = keyword
      ? orders.filter((o) => {
          return (
            o.orderNo.toLowerCase().includes(keyword) ||
            o.receiverName.toLowerCase().includes(keyword) ||
            (o.receiverPhone && o.receiverPhone.includes(keyword)) ||
            o.receiverAddress.toLowerCase().includes(keyword)
          );
        })
      : orders;
    return sortOrdersByPriority(filtered, target);
  }, [orders, filterText, target]);

  const matchCount = useMemo(() => {
    if (!orders) return 0;
    return orders.filter((o) => getOrderMatchDetails(o, target).isMatched).length;
  }, [orders, target]);

  return (
    <div className="mt-1.5 rounded-xl border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex gap-2">
        <input
          aria-label="搜索未完成订单"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="搜索未完成订单：单号 / 名字 / 电话 / 地址"
          className="h-9 flex-1 rounded-lg border border-slate-300 px-2.5 text-xs
                     dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        />
        {filterText && (
          <button
            onClick={() => setFilterText('')}
            className="h-9 rounded-lg border border-slate-200 px-2.5 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            重置
          </button>
        )}
        <button
          onClick={onClose}
          className="h-9 w-9 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          title="关闭"
        >
          <InlineIcon icon={X} />
        </button>
      </div>

      {busy && (
        <p className="mt-2 text-center text-xs text-slate-400">正在获取当前尚未完成的订单…</p>
      )}

      {err && <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">{err}</p>}

      {!busy && orders && (
        <div className="mt-1.5 flex items-center justify-between px-0.5 text-[11px] text-slate-500">
          <span>
            当前未完成订单 ({displayedOrders.length} / {orders.length})
            {matchCount > 0 && (
              <span className="ml-1.5 font-medium text-brand-600 dark:text-brand-400">
                · {matchCount} 单匹配
              </span>
            )}
          </span>
          <span className="text-[10px] text-slate-400">
            {matchCount > 0 ? '优先显示信息匹配订单' : '按订单号排序'}
          </span>
        </div>
      )}

      {!busy && displayedOrders.length > 0 && (
        <ul className="mt-1.5 max-h-64 space-y-1.5 overflow-y-auto pr-0.5">
          {displayedOrders.map((o) => {
            const match = getOrderMatchDetails(o, target);
            const statusLabel =
              (ADMIN_STATUS_LABEL as Record<string, string>)[o.status] || o.status;

            return (
              <li key={o.orderNo}>
                <button
                  type="button"
                  onClick={() => onPick(o.orderNo)}
                  className={`w-full rounded-lg border p-2 text-left text-xs transition-colors ${
                    match.isMatched
                      ? 'border-brand-300 bg-brand-50/60 hover:bg-brand-100/60 dark:border-brand-700 dark:bg-brand-950/30 dark:hover:bg-brand-950/60'
                      : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/60'
                  }`}
                >
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">
                        {o.orderNo}
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        {o.receiverName}
                      </span>
                      {o.receiverPhone && (
                        <span className="font-mono text-[11px] text-slate-500">
                          {o.receiverPhone}
                        </span>
                      )}
                    </div>
                    <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                      {statusLabel}
                    </span>
                  </div>

                  {match.isMatched && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {match.phoneMatch && (
                        <span className="rounded bg-emerald-100 px-1.5 py-0.2 text-[10px] font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                          电话一致
                        </span>
                      )}
                      {match.nameMatch && (
                        <span className="rounded bg-sky-100 px-1.5 py-0.2 text-[10px] font-medium text-sky-800 dark:bg-sky-950 dark:text-sky-300">
                          名字一致
                        </span>
                      )}
                      {match.coordMatch && (
                        <span className="rounded bg-purple-100 px-1.5 py-0.2 text-[10px] font-medium text-purple-800 dark:bg-purple-950 dark:text-purple-300">
                          坐标一致
                        </span>
                      )}
                    </div>
                  )}

                  <span className="mt-1 block text-slate-600 dark:text-slate-400">
                    {o.receiverAddress}
                  </span>
                  <div className="mt-0.5 text-[10px] text-slate-400">
                    {new Date(o.createdAt).toLocaleDateString('zh-CN')}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!busy && orders && displayedOrders.length === 0 && filterText && (
        <p className="mt-2 text-center text-xs text-slate-400">
          未完成订单中没有找到与「{filterText}」匹配的单
        </p>
      )}
    </div>
  );
}
